'use client';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import {
	Box,
	Button,
	Drawer,
	Fade,
	FormControl,
	IconButton,
	MenuItem,
	Select,
	SelectChangeEvent,
	TextareaAutosize,
	Typography,
} from '@mui/material';
import { CSSProperties, FC, useCallback, useMemo, useState } from 'react';

import * as SURVEY_CONSTANTS from '@/components/content/HdmAditionalInfo/parts/constants_surveymonkey';
import { styles } from '@/components/content/HdmAditionalInfo/styles/style';
import { buildSurveyPayload } from '@/components/content/HdmAditionalInfo/utils/buildSurveyPayload';
import { useLocalization } from '@/data/Localization';

interface SelectDropdownDrawerProps {
	open: boolean;
	closeEv: (event: any) => void; // Standardizing the event type
	skuNumber?: string;
	productTitle?: string;
	departmentPDP?: string;
}

type StatusKind = 'success' | 'validation' | 'network' | 'server';
type SubmitStatus = null | { type: StatusKind; message: string };

export const SelectDropdownDrawer: FC<SelectDropdownDrawerProps> = ({
	open,
	closeEv,
	skuNumber,
	productTitle,
	departmentPDP,
}) => {
	const productDetailsText = useLocalization('HDMProductDetail');

	const ISSUE_TYPES = useMemo(
		() => [
			{
				value: 'op1_compras',
				label: productDetailsText.Purchases.t(),
				defaultSub: SURVEY_CONSTANTS.ID_COMPRAS_NotAbleAddToCart,
			},
			{
				value: 'op2_info',
				label: productDetailsText.IncorrectInformation.t(),
				defaultSub: SURVEY_CONSTANTS.ID_INFO_ProductTitle,
			},
		],
		[productDetailsText]
	);

	const SUB_OPTIONS = useMemo(
		() => ({
			op1_compras: [
				{
					value: SURVEY_CONSTANTS.ID_COMPRAS_NotAbleAddToCart,
					label: productDetailsText.NotAbleAddToCart.t(),
				},
				{
					value: SURVEY_CONSTANTS.ID_COMPRAS_ProductArrivedDamaged,
					label: productDetailsText.ProductArrivedDamaged.t(),
				},
				{
					value: SURVEY_CONSTANTS.ID_COMPRAS_ProductAvailability,
					label: productDetailsText.ProductAvailability.t(),
				},
				{ value: SURVEY_CONSTANTS.ID_COMPRAS_Other, label: productDetailsText.Other.t() },
			],
			op2_info: [
				{
					value: SURVEY_CONSTANTS.ID_INFO_ProductTitle,
					label: productDetailsText.ProductTitle.t(),
				},
				{
					value: SURVEY_CONSTANTS.ID_INFO_ProductDescription,
					label: productDetailsText.ProductDescription.t(),
				},
				{
					value: SURVEY_CONSTANTS.ID_INFO_ProductImage,
					label: productDetailsText.ProductImage.t(),
				},
				{
					value: SURVEY_CONSTANTS.ID_INFO_TechnicalSpecifications,
					label: productDetailsText.TechnicalSpecifications.t(),
				},
				{
					value: SURVEY_CONSTANTS.ID_INFO_MaterialCalculator,
					label: productDetailsText.MaterialCalculator.t(),
				},
				{ value: SURVEY_CONSTANTS.ID_INFO_Other, label: productDetailsText.Other.t() },
			],
		}),
		[productDetailsText]
	);

	const [issueType, setIssueType] = useState<string>('');
	const [subIssueId, setSubIssueId] = useState<string>('');
	const [message, setMessage] = useState<string>('');
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const [submitStatus, setSubmitStatus] = useState<SubmitStatus>(null);
	const handleIssueTypeChange = (event: SelectChangeEvent<string>) => {
		const val = event.target.value;
		setIssueType(val);
		// Set default sub-issue automatically
		const config = ISSUE_TYPES.find((i) => i.value === val);
		setSubIssueId('');
	};

	const handleSubmit = useCallback(
		async (e: React.FormEvent) => {
			e.preventDefault();
			if (!issueType || !subIssueId || !message) return;

			setIsSubmitting(true);
			setSubmitStatus(null);

			const payload = buildSurveyPayload({
				message,
				issueType,
				subIssueId,
				skuNumber,
				productTitle,
				departmentPDP,
			});

			try {
				const response = await fetch('/api/survey', {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify(payload),
				});

				const result = await response.json();

				if (response.ok && result.response_status === SURVEY_CONSTANTS.COMPLETED) {
					setSubmitStatus({ type: 'success', message: productDetailsText.DrawerSubmitSuccess.t() });
					setIssueType('');
					setSubIssueId('');
					setMessage('');
					setTimeout(() => closeEv(false), 2000);
				} else {
					throw new Error('Submission failed');
				}
			} catch (error) {
				setSubmitStatus({ type: 'network', message: productDetailsText.DrawerSubmitError.t() });
			} finally {
				setIsSubmitting(false);
			}
		},
		[issueType, subIssueId, message, skuNumber, productTitle, productDetailsText, closeEv]
	);

	const currentSubOptions = SUB_OPTIONS[issueType as keyof typeof SUB_OPTIONS] || [];

	return (
		<Drawer anchor="right" open={open} onClose={closeEv} PaperProps={{ sx: styles.drawContainer }}>
			<Box component="form" onSubmit={handleSubmit} sx={styles.drawerFormContainer}>
				<Box sx={styles.drawerFormHeader}>
					<IconButton onClick={() => closeEv(false)} sx={{ position: 'absolute', left: 0 }}>
						<HighlightOffIcon fontSize="small" />
					</IconButton>
					<Typography variant="h6" fontWeight="600">
						{productDetailsText.ReportProblem.t()}
					</Typography>
				</Box>
				<FormControl fullWidth sx={{ mb: 2 }}>
					<Select
						value={issueType}
						onChange={handleIssueTypeChange}
						displayEmpty
						inputProps={{ 'aria-label': productDetailsText.TypeOfProblem.t() }}
					>
						<MenuItem value="" disabled>
							{productDetailsText.TypeOfProblem.t()}
						</MenuItem>
						{ISSUE_TYPES.map((opt) => (
							<MenuItem key={opt.value} value={opt.value}>
								{opt.label}
							</MenuItem>
						))}
					</Select>
				</FormControl>
				{issueType && (
					<FormControl fullWidth sx={{ mb: 2 }}>
						<Select value={subIssueId} onChange={(e) => setSubIssueId(e.target.value)} displayEmpty>
							<MenuItem value="" disabled>
								{issueType === 'op1_compras'
									? productDetailsText.SelectSpecificProblem.t()
									: productDetailsText.WhereDidYouFoundTheProblem.t()}
							</MenuItem>
							{currentSubOptions.map((opt) => (
								<MenuItem key={opt.value} value={opt.value}>
									{opt.label}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				)}
				<TextareaAutosize
					placeholder={productDetailsText.TellUsTheProblem.t()}
					minRows={4}
					value={message}
					onChange={(e) => setMessage(e.target.value)}
					style={styles.drawerFormTextArea as CSSProperties}
				/>
				<Button
					variant="contained"
					type="submit"
					fullWidth
					disabled={!issueType || !subIssueId || !message || isSubmitting}
					sx={{ mt: 2 }}
				>
					{isSubmitting ? productDetailsText.Sending.t() : productDetailsText.SentSuggestion.t()}
				</Button>

				<Fade in={!!submitStatus}>
					<Typography
						variant="caption"
						sx={submitStatus?.type === 'success' ? styles.msgSuccess : styles.msgError}
					>
						{submitStatus?.message}
					</Typography>
				</Fade>
			</Box>
		</Drawer>
	);
};
